
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/Navbar';
import Footer from '../../home/components/Footer';

export default function RadlettPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Electrician in Radlett | Beyond Electrical | NICEIC Approved';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Trusted NICEIC approved electrician serving Radlett and surrounding areas. 15+ years experience. Same day service available. Call 020 3198 1818 for a free quote.');
    }
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', 'https://beyondelectrical.co.uk/electrician-radlett');
    }
  }, []);

  const services = [
    { name: 'Electrical Installations', link: '/services/installations/electrical-installations', icon: 'ri-flashlight-line' },
    { name: 'Full & Partial Rewiring', link: '/services/installations/rewiring', icon: 'ri-git-branch-line' },
    { name: 'Consumer Unit Upgrades', link: '/services/installations/consumer-unit-upgrades', icon: 'ri-dashboard-line' },
    { name: 'EV Charger Installation', link: '/services/installations/ev-charger', icon: 'ri-charging-pile-2-line' },
    { name: 'EICR Testing', link: '/services/safety-compliance/eicr-testing', icon: 'ri-file-list-3-line' },
    { name: 'Fault Finding & Repairs', link: '/services/repairs/fault-finding', icon: 'ri-search-line' },
    { name: 'Lighting Installation', link: '/services/installations/lighting-installation', icon: 'ri-lightbulb-line' },
    { name: 'Smart Home Systems', link: '/services/installations/smart-home', icon: 'ri-home-smile-line' },
    { name: 'Emergency Callouts', link: '/services/repairs/emergency-callouts', icon: 'ri-alarm-warning-line' },
  ];

  const whyChooseUs = [
    { icon: 'ri-award-line', title: '15 Years Experience', description: 'Trusted expertise in all electrical work' },
    { icon: 'ri-shield-check-line', title: 'NICEIC Approved', description: 'Fully qualified and certified electricians' },
    { icon: 'ri-time-line', title: 'Same Day Service', description: 'Fast response for urgent electrical needs' },
    { icon: 'ri-price-tag-3-line', title: 'Fixed Pricing', description: 'No hidden costs or surprise charges' },
  ];

  const surroundingAreas = ['Borehamwood', 'Elstree', 'Shenley', 'Aldenham', 'Bushey', 'Watford', 'St Albans', 'London Colney'];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white pt-32 pb-20">
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="flex flex-wrap gap-3 mb-6">
            <span className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm border border-white/20">
              <i className="ri-shield-check-line text-amber-400"></i>
              NICEIC Approved
            </span>
            <span className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm border border-white/20">
              <i className="ri-map-pin-line text-amber-400"></i>
              Local to Radlett
            </span>
          </div>
          <h1 className="text-5xl font-bold mb-6">
            Electrician in <span className="text-amber-400">Radlett</span>
          </h1>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl">
            Trusted NICEIC approved electrician serving Radlett and surrounding areas. From installations to emergency repairs, we deliver safe, reliable electrical solutions.
          </p>
          <div className="flex flex-wrap gap-4">
            <a href="tel:02031981818" className="inline-flex items-center gap-2 bg-amber-400 text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-amber-500 transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-phone-line"></i>
              Call 020 3198 1818
            </a>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/20 transition-colors border border-white/20 whitespace-nowrap cursor-pointer">
              Get Free Quote
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Services in Radlett</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Comprehensive electrical services for homes and businesses across Radlett</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <Link key={index} to={service.link} className="group bg-white border border-gray-200 rounded-lg p-6 hover:border-amber-400 hover:shadow-lg transition-all cursor-pointer">
                <div className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded-lg mb-4 group-hover:bg-amber-400/10 transition-colors">
                  <i className={`${service.icon} text-2xl text-amber-400`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-amber-400 transition-colors">{service.name}</h3>
                <div className="flex items-center text-amber-400 font-medium">
                  Learn More <i className="ri-arrow-right-line ml-2"></i>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Why Choose Beyond Electrical?</h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">Your trusted local electrician in Radlett</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((item, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-6 text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-amber-400/10 rounded-full mx-auto mb-4">
                  <i className={`${item.icon} text-3xl text-amber-400`}></i>
                </div>
                <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                <p className="text-gray-300">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Areas We Cover</h2>
            <p className="text-lg text-gray-600">Serving Radlett and surrounding areas</p>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {surroundingAreas.map((area, index) => (
              <span key={index} className="bg-white border border-gray-200 px-6 py-3 rounded-full text-gray-700 font-medium">{area}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">Need an Electrician in Radlett?</h2>
          <p className="text-xl text-gray-600 mb-8">Contact us today for a free, no-obligation quote</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="tel:02031981818" className="inline-flex items-center gap-2 bg-amber-400 text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-amber-500 transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-phone-line"></i>
              Call 020 3198 1818
            </a>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-gray-900 text-white px-8 py-4 rounded-lg font-semibold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer">
              Get Free Quote
            </Link>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
